import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import {
  buildAllProducts,
  mergeTaxonomyFromApi,
  normalizeCategoryList,
  normalizeProductSlugKeys,
} from './catalogNormalize';

const EMPTY_PRODUCTS = {};

function parseCatalogResponse(text) {
  const trimmed = String(text || '')
    .replace(/^\uFEFF/, '')
    .trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    const i = trimmed.indexOf('{');
    const j = trimmed.lastIndexOf('}');
    if (i >= 0 && j > i) {
      return JSON.parse(trimmed.slice(i, j + 1));
    }
    throw new Error('invalid-json');
  }
}

function isMysqlCatalogPayload(data) {
  return (
    data &&
    data.products &&
    typeof data.products === 'object' &&
    !Array.isArray(data.products) &&
    (data.ok === true ||
      data.ok === 1 ||
      data.ok === 'true' ||
      data.success === true ||
      data.success === 1)
  );
}

export function useProducts() {
  const location = useLocation();
  const { ready, isAdmin, token } = useAuth();
  const loadGenRef = useRef(0);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState(EMPTY_PRODUCTS);
  const [categories, setCategories] = useState([]);
  const [catalogDbActive, setCatalogDbActive] = useState(false);
  const [dataSource, setDataSource] = useState('none');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCatalog = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);

    const isStale = () => gen !== loadGenRef.current;

    try {
      const useAdminCatalog =
        location.pathname.startsWith('/admin') &&
        ready &&
        isAdmin &&
        String(token || '').trim() !== '';
      const catalogUrl = useAdminCatalog ? '/api/admin_catalog.php' : '/api/catalog.php';
      const catalogFetchOpts = useAdminCatalog
        ? {
            cache: 'no-store',
            headers: {
              Authorization: `Bearer ${String(token || '').trim()}`,
              'X-Admin-Session': String(token || '').trim(),
            },
          }
        : { cache: 'no-store' };

      const r = await fetch(catalogUrl, catalogFetchOpts);
      const text = await r.text();
      let data = null;
      try {
        data = parseCatalogResponse(text);
      } catch {
        throw new Error(
          !r.ok
            ? `${catalogUrl} returned HTTP ${r.status}. Run npm run php:serve with npm start.`
            : `Invalid JSON from ${catalogUrl}. Check PHP warnings in the response.`
        );
      }

      if (!isMysqlCatalogPayload(data)) {
        const msg =
          (data && typeof data.error === 'string' && data.error) ||
          (data && !r.ok ? `Catalog API HTTP ${r.status}.` : null) ||
          'Catalog API did not return MySQL data. Configure includes/db_config.php and run catalog migrations.';
        throw new Error(msg);
      }

      const mergedProducts = normalizeProductSlugKeys(data.products);
      const base = mergeTaxonomyFromApi(data.categories, mergedProducts);
      normalizeCategoryList(base, mergedProducts).forEach((c) => {
        if (!Array.isArray(mergedProducts[c.slug])) {
          mergedProducts[c.slug] = [];
        }
      });

      if (!isStale()) {
        setProducts(mergedProducts);
        setCategories(normalizeCategoryList(base, mergedProducts));
        setAllProducts(buildAllProducts(mergedProducts));
        setCatalogDbActive(true);
        setDataSource('mysql');
        setError(null);
      }
    } catch (e) {
      if (!isStale()) {
        setProducts(EMPTY_PRODUCTS);
        setCategories([]);
        setAllProducts([]);
        setCatalogDbActive(false);
        setDataSource('none');
        setError(e.message || 'Could not load catalog from database.');
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [location.pathname, ready, isAdmin, token]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadCatalog();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadCatalog]);

  return {
    allProducts,
    products,
    categories,
    catalogDbActive,
    dataSource,
    loading,
    error,
    refetch: loadCatalog,
  };
}
